import { useMemo } from 'react'
import { RemoveNull } from 'helpers/types'
import { identity, pickBy } from 'lodash'
import { useGetOrderListQuery, useSearchOrdersQuery } from 'modules/order/api'

import { prepareOrderListQuery, prepareOrderSearchQuery } from '../helpers'
import { OrderListFilters, TOption } from '../types'

interface UseOrderListDataProps {
  departure?: string | null
  departureDateFrom?: string | null
  departureDateTo?: string | null
  destination?: string | null
  mode?: 'filter' | 'search'
  page?: number
  paymentStatus?: TOption[] | null
  perPage?: number
  search?: string | null
  status?: TOption[] | null
}

export const useOrderListData = (props: UseOrderListDataProps) => {
  const {
    departure,
    departureDateFrom,
    departureDateTo,
    destination,
    mode = 'filter',
    page = 1,
    paymentStatus,
    perPage = 10,
    search,
    status
  } = props

  const filter = useMemo(() => {
    const paymentsStatusNames = paymentStatus?.map((el) => el.name)
    const statusNames = status?.map((el) => el.name)
    const allFilters: OrderListFilters = {
      page,
      perPage
    }

    if (mode === 'filter') {
      allFilters.departure = departure
      allFilters.destination = destination
      allFilters.departureDateFrom = departureDateFrom
      allFilters.departureDateTo = departureDateTo
      allFilters.status = statusNames?.length ? statusNames : undefined
      allFilters.paymentStatus = paymentsStatusNames?.length ? paymentsStatusNames : undefined
    }

    if (mode === 'search') {
      // Игнорируем поиск менее 3 символов
      if (search && search.length >= 3) {
        allFilters.search = search
      } else {
        allFilters.search = undefined
      }
    }

    return pickBy(allFilters, identity) as Required<RemoveNull<OrderListFilters>>
  }, [departure, departureDateFrom, departureDateTo, destination, mode, page, paymentStatus, perPage, search, status])

  const shouldUseSearch = mode === 'search'

  const filterQuery = useGetOrderListQuery(prepareOrderListQuery(filter), { skip: shouldUseSearch })

  const searchQuery = useSearchOrdersQuery(prepareOrderSearchQuery(filter), { skip: !shouldUseSearch })

  const data = shouldUseSearch ? searchQuery.data : filterQuery.data
  const isFetching = shouldUseSearch ? searchQuery.isFetching : filterQuery.isFetching
  const refetch = shouldUseSearch ? searchQuery.refetch : filterQuery.refetch

  const paginationFromData = data?.data.pagination
  const pagination = useMemo(
    () => ({
      page: paginationFromData?.page ?? page,
      perPage: paginationFromData?.perPage ?? perPage,
      total: paginationFromData?.total ?? 0
    }),
    [paginationFromData, page, perPage]
  )

  return {
    data,
    isFetching,
    refetch,
    filter,
    pagination
  }
}
